import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData
} from "gatsby-source-contentful/rich-text"
import {
  RichTextBody,
  contentTypes as ct
} from "@life-without-barriers/gatsby-common"

import { contentfulHelpers as Contentful } from "@life-without-barriers/utilities"
import { IGatsbyImageData } from "gatsby-plugin-image"
import { graphql } from "gatsby"

export interface NavJson {
  edges: [
    {
      node: Nav
    }
  ]
}

export interface Nav {
  title: string
  items: NavItem[]
}

export interface NavItem {
  title: string
  href: string
  icon: string
  color: string
}

export interface Homepage {
  campaign1: HomepageCampaign
  campaign2: HomepageCampaign
  featuredNews: ArticleSummary[]
}

export interface HomepageCampaign {
  heading: string
  description: {
    text: string
    childMarkdownRemark: {
      html: string
    }
  }
  buttonLabel: string
  url: string
  image: Contentful.RemoteImageAsset
  category: string
}

export interface HomepageOpenPosition {
  title: string
  url: string
}

export interface HomepageCareersPanel {
  openPosition1: HomepageOpenPosition
  openPosition2: HomepageOpenPosition
  openPosition3: HomepageOpenPosition
}

export interface ArticleSummary {
  slug: string
  category: string
  title: string
  introductionText: {
    text: string
  }
  featureImage: Contentful.RemoteImageAsset
}

// from our-approach - markdown specific
interface Resource {
  name: string
  copy: string
  reference: {
    publicURL: string
  }
  image: IGatsbyImageData
}

interface Frontmatter {
  slug: string
  title: string
  metaDescription: string
  resources: Resource[]
}

interface TemplateMarkdown {
  frontmatter: Frontmatter
  html: string
}

export interface CurrentPage {
  edges: [
    {
      node: TemplateMarkdown
    }
  ]
}

export interface PageResource {
  file: {
    url: string
  }
  title: string
}

export const ContentMarkdownQueryFragment = graphql`
  fragment ContentMarkdownQueryFragment on Query {
    currentPage: markdownRemark(fields: { slug: { eq: $slug } }) {
      html
      frontmatter {
        title
        contentTitle
        intro
        metaTitle
        metaDescription
        contactUsEmail
        contactUsComment
        resources {
          name
          reference {
            publicURL
          }
        }
        videoSection {
          youTubeVideoId
          title
          text
        }
      }
      fields {
        slug
      }
    }
    ogImage: file(relativePath: { regex: "/img-index-banner.jpg/" }) {
      childImageSharp {
        gatsbyImageData(width: 1200, quality: 90)
      }
    }
    site {
      siteMetadata {
        title
        siteUrl
      }
    }
  }
`
export interface ResourceJson {
  title: string
  name: string
  href?: string
  file?: {
    publicURL: string
  }
  // different to services shape...
  image: { childImageSharp: { gatsbyImageData: IGatsbyImageData } }
  theme?: string
  summarySentence?: string
  copy?: string
  mobileCopy?: string
  standAlone?: boolean
  external?: boolean
  imageObjectFit?: string
  order?: number
  brochureName?: string
  subsite?: string
}

export interface ResourcesJson {
  edges: [
    {
      node: ResourceJson
    }
  ]
}

type PageReferences = ContentfulPageReferences & Event

export interface ContentfulPageResources {
  title: string
  pageReferences: PageReferences[]
}

export interface TextHtml {
  childMarkdownRemark: {
    html: string
    rawMarkdownBody: string
  }
}

export interface ContentfulPageReferences {
  type: string
}

export interface Event {
  eventId: string
  name: string
  description?: TextHtml
  location: TextHtml
  buttonLabel?: string
  buttonUrl?: string
  startDateTime: string
  endDateTime?: string
}

export interface LocationSupportsItem {
  id: string
  supportTitle: string
  supportIcon: string
  responseTitle: string
  responseBody: string
  responseIcon: string
  responseCta: string
  serviceUrl: string
}

export interface LocationSupportsItemsJson {
  edges: [
    {
      node: LocationSupportsItem
    }
  ]
}

export interface Location {
  town: string
  state: string
  stateLong: string
  postcode: string
}

export interface SilListingContactDetails {
  phoneNumber: string
  emailAddress: string
}

export interface SilListingPartnerLogo {
  title: string
  image: Contentful.RemoteImageAsset
  imageLinkUrl: string
}

export interface RichTextHtml {
  childMarkdownRemark: {
    html: string
  }
}

export interface SilHouseVideo {
  title: string
  url: string
  youtubeDescription?: RichTextHtml
}

export type SdaCategory = "General" | undefined

export interface SilListing {
  id: string
  title: string
  listingDescription?: string
  state: string
  bedrooms: number
  bathrooms: number
  accessible: string
  sdaCategory: SdaCategory
  featureImage: Contentful.RemoteImageAsset
  galleryImages?: Contentful.RemoteImageAsset[]
  contactDetails: SilListingContactDetails
  partnerLogo?: SilListingPartnerLogo
  introduction: RichTextHtml
  roomInformation?: RichTextHtml
  houseInformation?: RichTextHtml
  housemates?: RichTextHtml
  locationInformation?: RichTextHtml
  accessibilityDescription?: RichTextHtml
  currentSupportModel?: RichTextHtml
  factorsToConsider?: RenderRichTextData<ContentfulRichTextGatsbyReference>
  silHouseVideo?: SilHouseVideo
  pdfBrochure?: Contentful.RemoteFileAsset
  location?: {
    lat: number
    lon: number
  }
}

export interface STAListing {
  id: string
  title: string
  slug: string
  region: string
  state: string
  displayEmail: string
  displayPhoneNumber: string
  listingDescription: string
  listingIntroductionText: RenderRichTextData<ContentfulRichTextGatsbyReference>
  featureImage: Contentful.RemoteImageAsset
  thingsToConsiderWhenStayingWithUs: RenderRichTextData<ContentfulRichTextGatsbyReference>
  galleryImages: Contentful.RemoteImageAsset[]
  accessibility: string[]
  ageSuitability: string[]
  lengthOfStay: string[]
  propertyFeatures: RenderRichTextData<ContentfulRichTextGatsbyReference>
  ourSupportModel: RenderRichTextData<ContentfulRichTextGatsbyReference>
  nearbyAttractions: RenderRichTextData<ContentfulRichTextGatsbyReference>
  nearbyAttractionsImage: Contentful.RemoteImageAsset
}

export interface RoyalCommissionPage {
  title: string
  subtitle: string
  body: RichTextBody
  contactInformation: RichTextBody
}

export const remoteImageAssetQuery = graphql`
  fragment RemoteContentfulImageAsset on ContentfulAsset {
    file {
      url
      fileName
      contentType
    }
    title
    description
    gatsbyImageData(width: 1200, quality: 90)
  }
`

export const smallRemoteImageAssetQuery = graphql`
  fragment SmallRemoteContentfulImageAsset on ContentfulAsset {
    file {
      url
      fileName
      contentType
    }
    title
    description
    gatsbyImageData(width: 400, quality: 90)
  }
`

export const forcedAspectRatioRemoteImageAssetQuery = graphql`
  fragment ForcedAspectRatioRemoteImageAssetQuery on ContentfulAsset {
    file {
      url
      fileName
      contentType
    }
    title
    description
    gatsbyImageData(quality: 90, layout: FULL_WIDTH, aspectRatio: 1.78)
  }
`

export const openGraphImageAssetQuery = graphql`
  fragment OpenGraphImageAssetQuery on ContentfulAsset {
    gatsbyImageData(formats: [JPG])
  }
`
export interface MediaArticleSummary {
  id: string
  slug: string
  title: string
  category: string
  introductionText: {
    text: string
  }
  date: string
}

export type MediaArticle = MediaArticleSummary & {
  body: MediaArticleBody
}

export type RemoteAssets =
  | Contentful.RemoteImageAsset
  | MediaCTATextLink
  | MediaCTAAssetDownload
  | MediaEvent

export interface MediaArticleBody {
  raw: string
  references: RemoteAssets[]
}

export interface MediaEvent extends Contentful.ContentfulAsset {
  name: string
  description?: string
  location: string
  buttonLabel?: string
  buttonUrl?: string
  startDateTime: string
  endDateTime?: string
}

export interface MediaCTATextLink extends Contentful.ContentfulAsset {
  label: string
  url: string
}

export interface MediaCTAAssetDownload extends Contentful.ContentfulAsset {
  label: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  asset: any
}

export const remoteAssetQuery = graphql`
  fragment RemoteContentfulMediaAsset on ContentfulAsset {
    contentful_id
    __typename
    file {
      url
      fileName
      contentType
    }
    title
    description
    gatsbyImageData(layout: CONSTRAINED)
  }
`

export const RemoteContentfulMediaCtaAssetDownload = graphql`
  fragment RemoteContentfulMediaCtaAssetDownload on ContentfulMediaCtaAssetDownload {
    contentful_id
    __typename
    id
    label
    asset {
      file {
        url
      }
    }
  }
`

export const RemoteContentfulMediaCtaTextLink = graphql`
  fragment RemoteContentfulMediaCtaTextLink on ContentfulMediaCtaTextLink {
    contentful_id
    __typename
    id
    label
    url
  }
`

export type SiteMetadata = ct.SiteMetaData
export type Site = ct.Site

export interface SummaryBarItem {
  id: number
  icon: string
  height: string
  title: string
  value: string
}

export interface ParagraphItem {
  header: string
  text: string
}

export interface CarerStoryContentful {
  storyId: string
  title: string
  bannerImage: Contentful.RemoteImageAsset
  subtitle: string
  description: string
  slug: string
  classNames?: string
}

export interface StoryTileProps {
  title: string
  subtitle: string
  summarySentence: string
  image: IGatsbyImageData
  imageAlt: string
  slug: string
}

export const pathFromContentfulSlug = (slug: string): string =>
  `/foster-care/stories/${slug}/`

export const contentfulStorySummaryToStoryTile = ({
  bannerImage,
  title,
  description,
  subtitle,
  slug
}: ct.FosterCareStorySummary): StoryTileProps => ({
  image: bannerImage.gatsbyImageData,
  imageAlt: bannerImage.description,
  title: title,
  summarySentence: description,
  subtitle: subtitle,
  slug: pathFromContentfulSlug(slug)
})

export interface MetaData {
  title: string
  description: string
}

export interface CareType {
  meta: MetaData
  title: string
  shortTitle: string
  icon: string
  type: string
  typeDisplay: string
  bannerImage: { childImageSharp: IGatsbyImageData }
  bannerImageAlt: string
  introduction: string
  why: string
  duration: string
  support: string
  relatedCopy: string
  path: string
  hideChildrenInCare: boolean | null
}

export interface AllTypesOfCareJson {
  edges: [
    {
      node: CareType
    }
  ]
}

export interface AllMarkdownRemark {
  edges: [
    {
      node: {
        html: string
        frontmatter: {
          slug: string
        }
      }
    }
  ]
}

export interface CarerSupportItem {
  header: string
  text: string
  exclude?: [string]
}

export interface CarerSupportItemsJson {
  edges: [
    {
      node: CarerSupportItem
    }
  ]
}

export interface PageAnnouncement {
  id: string
  page: string
  boldText?: string
  subText: string
  ctaLabel?: string
  ctaUrl?: string
  __typename: string
}

export const fosterCareRemoteImageAssetQuery = graphql`
  fragment FosterCareRemoteImageAssetQuery on ContentfulAsset {
    file {
      url
      fileName
      contentType
    }
    title
    description
    gatsbyImageData(quality: 90, width: 600, aspectRatio: 1.78)
  }
`

export interface LocationPageParameters extends MapLocation {
  enteredAddress?: string
}

export interface MapLocation {
  lat: number
  lon: number
  address?: string
}

export interface ContactUsLocation {
  id: string
  streetAddress: string
  town: string
  state: string
  postcode: string
  openingHours: string
  location: MapLocation
  description?: {
    childMarkdownRemark: {
      rawMarkdownBody: string
      html: string
    }
  }
  phoneNumber: string
  emailAddress?: string
}
export interface LocationPageParameters extends MapLocation {
  enteredAddress?: string
}
